/* eslint-disable */
import { faArrowRightArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Table as BSTable, Container } from 'react-bootstrap';
import React, { CSSProperties, ReactNode, useEffect, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useParams } from 'react-router-dom';
import { ActionButtons } from './ActionButtons';
import { s } from '../../app/helper';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { TBlobPair, fetchAllMatches, fetchNextMatches, selectBlobsSlice } from '../../reducer/blobsSlice';

const Th = ({ className = '', children }: { className?: string, children?: ReactNode }): JSX.Element => (
  <th className={s('sticky-top bg-secondary text-white p-2', className)}>{children}</th>
);

const Td = ({ className = '', style, children }: { className?: string, style?: CSSProperties, children?: ReactNode }): JSX.Element => (
  <td className={s('align-middle border px-4', className)} style={style}>{children}</td>
);

const Tr: React.FC<TBlobPair & { index: number, style?: CSSProperties }> = ({ de, en, style, ...blob_data }) => (
  <tr style={style}>
    <td className="p-5 border-0" />

    <Td className="text-center">{de.page}</Td>
    <Td className="py-2" style={{ width: 550 }}>
      {de.content}
    </Td>

    <Td
      className="text-white ps-5 py-2"
      style={{ backgroundColor: 'var(--redish-bg)', position: 'relative', width: 550 }}
    >
      <FontAwesomeIcon
        icon={faArrowRightArrowLeft}
        className="rounded-circle bg-black text-white p-2"
        style={{ position: 'absolute', top: '35%', left: '-15px' }}
      />
      <p>
        {en.content}
      </p>
    </Td>
    <Td className="text-center text-white" style={{ backgroundColor: 'var(--redish-bg)' }}>{en.page}</Td>

    <td style={{ width: 360 }}><ActionButtons {...blob_data} /></td>
  </tr>
);

export function MatchTable(): JSX.Element {
  const { reportId } = useParams();
  const dispatch = useAppDispatch();
  useEffect(() => { dispatch(fetchAllMatches()) }, [dispatch, reportId]);

  const blobsSlice = useAppSelector(selectBlobsSlice);
  const matches: TBlobPair[] = [];
  if (blobsSlice.status === 'success' || blobsSlice.status === 'loading') {
    matches.push(...blobsSlice.data);
  }

  const parentRef = useRef<HTMLTableSectionElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: matches.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 35,
    // overscan: 5,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  const [isFetchingNext, setIsFetchingNext] = React.useState(false);

  useEffect(() => {
    if (!isFetchingNext && blobsSlice.status === 'success') {
      setTimeout(() => {
        if (virtualItems.length === 0 || virtualItems[virtualItems.length - 1].index >= matches.length - 1) {
          console.count('fetchNextMatches');
          setIsFetchingNext(true);
          dispatch(fetchNextMatches()).then(() => setIsFetchingNext(false));
        }
      }, 100); // because virtualItems.length === 0  at the beginning
    }
  }, [blobsSlice.status, virtualItems])

  return (
    <Container fluid className="overflow-y-auto p-0" style={{ height: 425 }} ref={parentRef}>
      <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}>
        <BSTable size="sm" className="border-0">
          <thead style={{
            position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1,
          }}
          >
            <tr>
              <Th />
              <Th>Seite</Th>
              <Th className="text-start">Quelltext</Th>
              <Th className="text-start">Übersetzungsfehler</Th>
              <Th>Seite</Th>
              <Th />
            </tr>
          </thead>
          <tbody className="table-bordered">
            {rowVirtualizer.getVirtualItems().map((virtualRow, index) => (
              matches[virtualRow.index].correctness !== false &&
              <Tr
                key={virtualRow.key}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...matches[virtualRow.index]}
                index={virtualRow.index}
                style={{
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start - index * virtualRow.size}px)`,
                }}
              />
            ))}
          </tbody>
        </BSTable>
      </div>
    </Container>
  );
}
